import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import {
  Container,
  SectionHeading,
  Reveal,
  Overline,
  GlassCard,
  AxorButton,
  EASE,
} from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import {
  PrismBackground,
  PrismNodes,
  PrismHero,
  PrismOutline,
} from "@/components/Prism";
import { CTASection } from "@/components/CTASection";

const whoWeAre = [
  "Career guidance",
  "Recruitment",
  "Professional preparation",
  "Industry understanding",
  "Technology",
];

const setsApart = [
  { title: "US Market Understanding", copy: "We know how hiring really works across the American landscape." },
  { title: "Personalized Support", copy: "Guidance shaped around your goals — never one-size-fits-all." },
  { title: "Career-Focused Approach", copy: "We invest in your growth, not just a single placement." },
  { title: "Technology + Human Expertise", copy: "Smart tools, guided by people who genuinely care." },
  { title: "Opportunity Matching", copy: "Aligning skills and ambition with the right roles." },
];

const approachSteps = ["Understand", "Prepare", "Connect", "Support"];

export default function About() {
  return (
    <>
      <PageHero
        overline="About Axor"
        title="Connecting Talent With Opportunity."
        highlight="Opportunity"
        description="Axor Technologies helps students and professionals across America navigate their careers and connect with meaningful opportunities."
        testid="about-hero"
      />

      {/* Mission */}
      <section className="relative overflow-hidden py-24 md:py-32" data-testid="about-mission">
        <PrismBackground />
        <Container className="relative grid items-center gap-14 lg:grid-cols-[1fr_0.75fr]">
          <div>
            <Reveal><Overline>Our Mission</Overline></Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-2xl text-2xl font-medium leading-[1.4] tracking-tight text-axor-white sm:text-3xl">
                We bridge the gap between ambitious talent and forward-thinking
                employers — helping people build careers while helping businesses
                find <span className="text-gradient-cyan">the right people.</span>
              </p>
            </Reveal>
          </div>
          <div className="relative flex justify-center">
            <div className="absolute -inset-4 rounded-full bg-axor-cyan/10 blur-3xl" />
            <PrismHero className="relative w-40" />
          </div>
        </Container>
      </section>

      {/* Who we are */}
      <section className="relative overflow-hidden bg-axor-deep py-24 md:py-32" data-testid="about-who">
        <PrismBackground />
        <Container className="relative">
          <SectionHeading
            overline="Who We Are"
            title="Career Expertise, Brought Together."
            highlight="Brought Together"
            description="Axor combines the capabilities that move careers forward."
          />
          <div className="mt-14 flex flex-wrap gap-4">
            {whoWeAre.map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
                className="flex items-center gap-3 rounded-full border border-white/[0.1] bg-white/[0.03] px-6 py-3.5 text-base font-medium text-axor-white"
              >
                <Check className="h-4 w-4 text-axor-cyan" />
                {item}
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* What sets us apart */}
      <section className="relative overflow-hidden py-24 md:py-32" data-testid="about-sets-apart">
        <PrismBackground />
        <Container className="relative">
          <SectionHeading
            overline="What Sets Us Apart"
            title="A Different Kind Of Career Partner."
            highlight="Career Partner"
          />
          <div className="mt-14 grid gap-6 md:grid-cols-12">
            {setsApart.map((s, i) => {
              const spans = ["md:col-span-5", "md:col-span-7", "md:col-span-4", "md:col-span-4", "md:col-span-4"];
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.1 }}
                  className={spans[i]}
                >
                  <GlassCard className="relative h-full overflow-hidden p-7">
                    <PrismOutline className="absolute -right-3 -top-3 opacity-20" size={70} />
                    <span className="text-sm font-bold text-axor-cyan">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-3 text-xl font-semibold tracking-tight text-axor-white">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-axor-text">{s.copy}</p>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Our approach */}
      <section className="relative overflow-hidden bg-axor-deep py-24 md:py-32" data-testid="about-approach">
        <PrismBackground />
        <Container className="relative grid items-center gap-14 lg:grid-cols-2">
          <div>
            <Reveal><Overline>Our Approach</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 text-4xl font-semibold leading-[1.1] tracking-tight text-axor-white sm:text-5xl">
                Relationships Over <span className="text-gradient-cyan">Quick Placements.</span>
              </h2>
            </Reveal>
            <div className="mt-10 space-y-3">
              {approachSteps.map((step, i) => (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, ease: EASE, delay: i * 0.12 }}
                  className="flex items-center gap-4"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-axor-cyan/25 bg-axor-cyan/10 text-sm font-bold text-axor-cyan">
                    {i + 1}
                  </span>
                  <span className="text-xl font-medium text-axor-white">{step}</span>
                  {i < approachSteps.length - 1 && (
                    <ArrowRight className="ml-auto h-4 w-4 text-axor-text sm:ml-0" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-axor-cyan/10 blur-3xl" />
            <PrismNodes className="relative" />
          </div>
        </Container>
      </section>

      {/* Success statement */}
      <section className="relative overflow-hidden py-20 md:py-28" data-testid="about-success">
        <PrismBackground />
        <Container className="relative text-center">
          <Reveal>
            <h2 className="mx-auto max-w-3xl text-4xl font-semibold leading-[1.1] tracking-tight text-axor-white sm:text-5xl">
              Your Success Is <span className="text-gradient-cyan">Our Success.</span>
            </h2>
          </Reveal>
        </Container>
      </section>

      <CTASection
        overline="Let's Talk"
        title="Ready To Take The Next Step?"
        description="Talk to Axor and discover how we can help you move forward."
        showJobs={false}
      />
    </>
  );
}
